import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    withStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../utils/axiosInstance';
import { endpoint, snackbarDefaultValue, snackbarVariant, tableTranslations } from '../../utils/constants';
import { padIpAddress } from '../../utils/fwHelpers';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Modal from '../Modal/Modal';
import Snackbar from '../Snackbar/Snackbar';
import TitleHeader from '../TitleHeader/TitleHeader';

const IPV4_REGEX = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

const DEFAULT_PORT = '4000';

const isValidIpv4 = value => {
    const match = IPV4_REGEX.exec(value.trim());
    if (!match) return false;

    return match.slice(1).every(octet => Number(octet) <= 255);
};

const isValidPort = value => {
    const port = value.trim();

    return /^\d+$/.test(port) && Number(port) >= 1 && Number(port) <= 65535;
};

const styles = {
    root: {
        height: '100%',
        boxSizing: 'border-box',
        padding: '1rem 2rem',
        display: 'flex',
        flexDirection: 'column',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '1rem',
        marginBottom: '1rem',
        alignItems: 'center',
    },
    grid: {
        fontFamily: 'inherit',
        width: '100%',
        flex: 1,
        minHeight: 0,
    },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        flex: 1,
    },
    field: {
        marginBottom: '1rem',
    },
};

const RowNumberRenderer = ({ node }) => node.rowIndex + 1;

const ActionsRenderer = ({ data, context }) => (
    <>
        <IconButton size="small" title="Edytuj APN" onClick={() => context.onEdit(data)}>
            <EditIcon />
        </IconButton>
        <IconButton size="small" title="Usuń APN" onClick={() => context.onDelete(data)}>
            <DeleteIcon />
        </IconButton>
    </>
);

const FwApns = ({ classes }) => {
    const [apns, setApns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editedApn, setEditedApn] = useState(null);
    const [form, setForm] = useState({ apn: '', ip: '', port: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [confirmApn, setConfirmApn] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [snackbarData, setSnackbarData] = useState(snackbarDefaultValue);

    const showMessage = (message, variant) => {
        setSnackbarData({ isSnackbarOpen: true, message, variant, errorInfo: '' });
    };

    const handleIsSnackbarOpen = () => setSnackbarData(snackbarDefaultValue);

    const fetchApns = () => {
        setIsLoading(true);
        axiosInstance
            .get(endpoint.FW_APNS)
            .then(res => setApns(res.data?.data ?? res.data ?? []))
            .catch(err =>
                showMessage(err.response?.data?.message || 'Błąd pobierania listy APN', snackbarVariant.ERROR)
            )
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        fetchApns();
    }, []);

    const openAddDialog = () => {
        setEditedApn(null);
        setForm({ apn: '', ip: '', port: DEFAULT_PORT });
        setIsDialogOpen(true);
    };

    const openEditDialog = apn => {
        setEditedApn(apn);
        setForm({ apn: apn.apn, ip: apn.ip, port: apn.port ? String(apn.port) : DEFAULT_PORT });
        setIsDialogOpen(true);
    };

    const closeDialog = () => setIsDialogOpen(false);

    const padFormIp = () => setForm(current => ({ ...current, ip: padIpAddress(current.ip) }));

    const validate = () => {
        if (!form.apn.trim()) {
            showMessage('Podaj nazwę APN', snackbarVariant.ERROR);
            return false;
        }
        if (!isValidIpv4(form.ip)) {
            showMessage('Podaj poprawny adres IP (IPv4)', snackbarVariant.ERROR);
            return false;
        }
        if (!isValidPort(form.port)) {
            showMessage('Podaj poprawny port (1-65535)', snackbarVariant.ERROR);
            return false;
        }

        return true;
    };

    const save = () => {
        if (!validate()) return;

        const payload = {
            apn: form.apn.trim(),
            ip: padIpAddress(form.ip),
            port: Number(form.port),
        };
        const request = editedApn
            ? axiosInstance.put(`${endpoint.FW_APNS}/${editedApn.id}`, payload)
            : axiosInstance.post(endpoint.FW_APNS, payload);

        setIsSaving(true);
        request
            .then(() => {
                showMessage(editedApn ? 'APN zaktualizowany' : 'APN dodany', snackbarVariant.SUCCESS);
                closeDialog();
                fetchApns();
            })
            .catch(err =>
                showMessage(err.response?.data?.message || 'Błąd zapisu APN', snackbarVariant.ERROR)
            )
            .finally(() => setIsSaving(false));
    };

    const removeApn = apn => {
        setIsDeleting(true);
        axiosInstance
            .delete(`${endpoint.FW_APNS}/${apn.id}`)
            .then(() => {
                showMessage('APN usunięty', snackbarVariant.SUCCESS);
                fetchApns();
            })
            .catch(err =>
                showMessage(err.response?.data?.message || 'Błąd usuwania APN', snackbarVariant.ERROR)
            )
            .finally(() => setIsDeleting(false));
    };

    const handleRemoveAnswer = confirmed => {
        const apn = confirmApn;
        setConfirmApn(null);
        if (confirmed && apn) {
            removeApn(apn);
        }
    };

    return (
        <div className={classes.root}>
            <TitleHeader title="Moduł FW — APN" />
            <div className={classes.actions}>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openAddDialog}>
                    Dodaj APN
                </Button>
            </div>
            {isLoading ? (
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            ) : (
                <div className={'ag-theme-alpine'} style={styles.grid}>
                    <AgGridReact
                        localeText={tableTranslations}
                        rowData={apns}
                        suppressCellSelection
                        pagination
                        paginationPageSize={50}
                        enableCellTextSelection
                        accentedSort
                        context={{ onEdit: openEditDialog, onDelete: setConfirmApn }}>
                        <AgGridColumn
                            headerName="Lp"
                            width={70}
                            sortable={false}
                            filter={false}
                            cellRendererFramework={RowNumberRenderer}
                        />
                        {/*
                            flex (not sizeColumnsToFit) keeps the grid filling the width across React re-renders
                        */}
                        <AgGridColumn field="apn" headerName="APN" flex={1} sortable filter resizable />
                        <AgGridColumn
                            field="ip"
                            headerName="Adres IP"
                            width={200}
                            sortable
                            filter
                            resizable
                        />
                        <AgGridColumn field="port" headerName="Port" width={110} sortable filter resizable />
                        <AgGridColumn
                            headerName=""
                            width={110}
                            sortable={false}
                            filter={false}
                            cellRendererFramework={ActionsRenderer}
                        />
                    </AgGridReact>
                </div>
            )}
            <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="xs">
                <DialogTitle>{editedApn ? 'Edytuj APN' : 'Nowy APN'}</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        className={classes.field}
                        label="Nazwa APN"
                        value={form.apn}
                        onChange={e => setForm(current => ({ ...current, apn: e.target.value }))}
                        variant="outlined"
                        fullWidth
                    />
                    <TextField
                        className={classes.field}
                        label="Adres IP"
                        value={form.ip}
                        onChange={e => setForm(current => ({ ...current, ip: e.target.value }))}
                        onBlur={padFormIp}
                        variant="outlined"
                        fullWidth
                    />
                    <TextField
                        label="Port"
                        value={form.port}
                        onChange={e => setForm(current => ({ ...current, port: e.target.value }))}
                        helperText={editedApn ? undefined : `Domyślnie ${DEFAULT_PORT}`}
                        variant="outlined"
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Anuluj</Button>
                    <Button color="primary" variant="contained" onClick={save} disabled={isSaving}>
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>
            <Modal
                isModalOpen={Boolean(confirmApn)}
                isSpinnerActive={isDeleting}
                handleAnswer={handleRemoveAnswer}
                modalMessage={confirmApn ? `Czy usunąć APN "${confirmApn.apn}"?` : ''}
            />
            <Snackbar snackbarData={snackbarData} handleIsSnackbarOpen={handleIsSnackbarOpen} />
        </div>
    );
};

export default withStyles(styles)(FwApns);
